import React, { useEffect, useState } from "react";
import { intl } from "LocaleWrapper";
import { useLazyQuery } from "@apollo/client";
import { IconButton, Tooltip, Alert } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LocationType } from "__generated__/graphql";
import { gql } from "__generated__/gql";
import { VoyageLink } from "utils/LinkUtils";
import { getLocationTypeNumber } from "view/locations/utils/LocationUtils";
import { StatusVoyageEntity } from "types/index";
import { StatusFragmentFragment as StatusFragment } from "__generated__/view/statuses/graphql/fragments.generated";

type StatusChangerLoadingLocationFieldProps = {
	status?: StatusFragment;
	entities: StatusVoyageEntity[];
	onChange: (valid: boolean) => void;
};

const StatusChangerLoadingLocationField = ({ status, entities, onChange }: StatusChangerLoadingLocationFieldProps) => {

	const isTerminalLocation = (type?: LocationType | null | number) => {
		return (type === LocationType.Terminal) || (type === getLocationTypeNumber(LocationType.Terminal));

	};

	const checkValid = (voyages: Pick<StatusVoyageEntity, "from" | "calls">[] | []) => {
		return voyages.length > 0 && voyages.every(v => {
			const validFrom = isTerminalLocation(v.from?.type);
			const validCall = v.calls[0]?.location ? isTerminalLocation(v.calls[0].location.type) : true;
			return validFrom && validCall;
		});
	};

	const [ isValid, setValid ] = useState(checkValid(entities));

	const [ getVoyage ] = useLazyQuery(VOYAGE_FROM_QUERY);

	useEffect(() => {
		onChange(isValid);
	}, [ isValid ]);

	if (!status?.shouldLoadOnTerminal) {
		return (
			<React.Fragment />
		);
	} else if (entities.length > 1 && !isValid) {
		return (
			<Alert severity="error">
				{intl.formatMessage({ id: "NOT_ALL_SELECTED_VOYAGES_HAS_VALID_LOCATION_TYPE" })}
			</Alert>
		);
	} else if (entities.length === 1 && !isValid) {
		return (
			<Alert
				severity="warning"
				action={
					<Tooltip title={intl.formatMessage({ id: "UPDATES_REFRESH" })} placement="top">
						<IconButton
							size="small"
							onClick={() => {
								getVoyage({
									variables: {
										id: entities[0].id,
									},
								}).then((response) => {
									const voyage = response.data?.voyage ;
									if (voyage) {
										setValid(checkValid([ voyage ]));
									}
								});
							}}>
							<RefreshIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				}>
				{intl.formatMessage({ id: "FIRST_CALL_MUST_BE_TERMINAL" })}

				<div>
					<VoyageLink voyage={entities[0]} />
				</div>
			</Alert>
		);
	} else {
		return (
			<Alert severity="success">
				{intl.formatMessage({ id: "ORDER_LOADING_PLACE" })}
			</Alert>
		);
	}
};

const VOYAGE_FROM_QUERY = gql(`#graphql
	query voyageFromQuery($id: Long!) {
		voyage(id: $id) {
			id
			voyageNumber
			from {
				id
				type
			}
			calls {
				location {
					id
					type
				}
			}
		}
	}
`);

export default StatusChangerLoadingLocationField;
